import { IContextualMenuItem } from '@fluentui/react';
import AppContext, { IAppContext } from 'App/AppContext';
import { IPContextualMenuItemType, IPContextualMenuProps } from 'baseComponents';
import { IPDefaultButton } from 'baseComponents/Button/IPButton';
import { getSearchableMenuProps } from 'components/Utils/SearchableMenuList';
import { newIcon } from 'globalStyles';
import { TFunction } from 'i18next';
import { DocumentProviderType, getProviderName } from 'models/documentProvider';
import DocumentOutputLocation from 'models/documentOutputLocation';
import ResourceList, { ResourceListType } from 'models/resourceList';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { isReportPurpose } from './outputLocationTexts';

//
// The category of an output location is chosen from a menu with the house keyword filter on top: "Create new category"
// pinned first, then the document library categories the purpose can use. Picking one hands it to the host, which
// binds it or keeps it as a template's own choice.
//

const createKey = 'createCategory';

const providerOf = (list: ResourceList): DocumentProviderType => list.providerType ?? DocumentProviderType.SharePoint;

/** A document library category with a bound root, on a provider the purpose allows. */
export function isEligibleCategory(location: DocumentOutputLocation, list: ResourceList): boolean {
  return (
    list.listType === ResourceListType.DocumentLibrary &&
    !!list.spDriveId &&
    !!list.spDriveItemId &&
    location.allowedProviders.includes(providerOf(list))
  );
}

/** "<name> (<provider>)", with the Word-only hint for a report purpose whose provider cannot make a PDF. */
export function getCategoryText(
  appContext: IAppContext,
  location: DocumentOutputLocation,
  list: ResourceList,
  t: TFunction<string[]>,
): string {
  const provider = getProviderName(providerOf(list));
  const convertsToPdf = appContext.globalDataCache.documentProviders.getCapabilities(providerOf(list)).pdfConversion;
  const wordOnly =
    isReportPurpose(location.purpose) && !convertsToPdf ? `, ${t('outputLocations:Hints.WordOnly')}` : '';

  return `${list.name} (${provider}${wordOnly})`;
}

export interface ICategoryMenuOptions {
  // The location whose purpose decides which categories can be used.
  location: DocumentOutputLocation;
  selectedListId?: number;
  onPick: (list: ResourceList) => void;
  onCreateNew: () => void;
}

export function getCategoryMenuProps(
  appContext: IAppContext,
  t: TFunction<string[]>,
  options: ICategoryMenuOptions,
): IPContextualMenuProps {
  const lists = appContext.globalDataCache.lists.items
    .filter((list) => isEligibleCategory(options.location, list))
    .map((list) => ({ list, text: getCategoryText(appContext, options.location, list, t) }))
    .sort((a, b) => a.text.localeCompare(b.text));

  const items: IContextualMenuItem[] = [
    {
      key: createKey,
      text: t('outputLocations:CategoryMenu.CreateNew'),
      iconProps: newIcon,
      onClick: () => options.onCreateNew(),
    },
    { key: 'divider', itemType: IPContextualMenuItemType.Divider },
    ...lists.map(({ list, text }) => ({
      key: String(list.listId),
      text,
      canCheck: true,
      isChecked: list.listId === options.selectedListId,
      onClick: () => options.onPick(list),
    })),
  ];

  //the create item is pinned, so it stays when the filter matches no category
  return { items, ...getSearchableMenuProps({ pinnedKeys: [createKey], minItemsForSearch: 1 }) };
}

interface ICategoryMenuButtonProps extends ICategoryMenuOptions {
  disabled?: boolean;
}

/** A button showing the chosen category, or "Choose a category", that opens the category menu. */
export const CategoryMenuButton = (props: ICategoryMenuButtonProps) => {
  const { t } = useTranslation(['outputLocations']);
  const appContext = useContext(AppContext);
  const selected = appContext.globalDataCache.lists.items.find((list) => list.listId === props.selectedListId);

  return (
    <IPDefaultButton
      text={selected ? selected.name : t('outputLocations:Evidence.ChooseCategory')}
      disabled={props.disabled}
      menuProps={getCategoryMenuProps(appContext, t as TFunction<string[]>, props)}
    />
  );
};
